<script lang="ts">
  import dayjs from 'dayjs';
  import { AppState } from '../../etc/stores';
  import Text from '../typography/Text.svelte';
  import PlusCircle from '../icons/PlusCircle.svelte';
  import MinusCircle from '../icons/MinusCircle.svelte';

  const actions = {
    pending: {
      text: 'Pending',
      style: 'transaction-pending'
    },
    earned: {
      text: 'Earned',
      style: 'transaction-earned'
    },
    convertedCoupon: {
      text: 'Converted coupon',
      style: 'transaction-converted-coupon'
    },
    redeemedCoupon: {
      text: 'Redeemed coupon',
      style: 'transaction-redeemed-coupon'
    }
  };

  enum TransactionOperations {
    INCREMENT = 'increment',
    DECREMENT = 'decrement',
    REFUND = 'refund',
    NONE = 'none',
  }

  enum TransactionStatus {
    SUCCESS = 'success',
    PENDING = 'pending',
    CANCELED = 'canceled',
  }

  type TransactionProps = {
    type: string;
    operation: TransactionOperations;
    updatedAt: string;
    bonusCoins: string;
    coins: string;
    message: string;
    connectTransaction: {};
    status: TransactionStatus;
  };

  export let data: TransactionProps;
  const {
    type,
    operation,
    updatedAt,
    bonusCoins,
    coins,
    message,
    status,
    connectTransaction
  } = data;

  // TODO: improve this method
  const getAction = () => {
    if (type === 'payment-auth') {
      return actions.pending;
    }

    if (operation === 'increment') {
      return actions.earned;
    }

    return actions.convertedCoupon;
  };
  const action = getAction();

  const canceled = status === 'canceled';
  const isProvisionalCanceled =
    canceled &&
    connectTransaction !== null &&
    connectTransaction.status === 'provisional';

  const getIconStatus = () => {
    if (canceled) {
      return {
        component: MinusCircle,
        fill: '#E32B2E'
      }
    }

    if (type === 'payment-auth') {
      return {
        component: MinusCircle,
        fill: '#F8A038'
      }
    }

    if (operation === TransactionOperations.DECREMENT) {
      return {
        component: MinusCircle,
        fill: '#E32B2E'
      }
    }

    if (operation === TransactionOperations.INCREMENT) {
      return {
        component: PlusCircle,
        fill: '#22B909'
      }
    }

    return null;
  }

  let learnMoreMessageVisible = false;

  const handleLearnMoreClick = () => {
    learnMoreMessageVisible = !learnMoreMessageVisible;
  };
</script>

<div
  class="transaction {action.style} {canceled
    ? 'transaction-provisional-canceled'
    : ''}"
>
  <div class="transaction-logo">
    <img
      class="transaction-business-logo"
      src={$AppState.business.logo.publicPath.small}
      alt={$AppState.business.name}
    />
    {#if getIconStatus()}
    <div class="transaction-action-icon">
      <svelte:component this={getIconStatus().component} fill={getIconStatus().fill} />
    </div>
    {/if}
  </div>
  <div class="transaction-status">
    <div class="transaction-info">
      <div class="transaction-business-name">
        <Text
          color="#012A5A"
          type="small"
          bold
          class="transaction-business-name-text">{$AppState.business.name}</Text
        >
      </div>
      <small class="transaction-date">{dayjs(updatedAt).format('MMM DD')}</small
      >
    </div>
    <Text color="#7A89B0" type="small" class="transaction-business-action"
      >{message}</Text
    >
    {#if isProvisionalCanceled}
      <div
        class="transaction-learn-more {learnMoreMessageVisible
          ? 'visible'
          : 'hidden'}"
      >
        <div
          class="transaction-learn-more-button"
          on:click={handleLearnMoreClick}
        >
          <Text type="small" color="#1E95FF" class="transaction-learn-more-text"
            >Learn more</Text
          >
          <img
            class="transaction-learn-more-svg"
            src={`${process.env.BASE_URL}/images/chevron-down.svg`}
            alt={message}
          />
        </div>
        <div class="transaction-learn-more-detail">
          <Text
            class="transaction-learn-more-detail-text"
            type="small"
            color="#000"
            >This transaction used a card previously linked to a different Twism
            account. Coins earned on this sale were sent to the owner of that
            account.</Text
          >
        </div>
      </div>
    {/if}
  </div>
</div>

<style lang="scss">
  :global(.transaction-business-name-text) {
    font-weight: 600 !important;
    font-size: 14px !important;
    line-height: 17px !important;
  }
  :global(.transaction-business-action) {
    font-weight: 400 !important;
    font-size: 14px !important;
    line-height: 18px !important;
  }
  :global(.transaction-learn-more-text) {
    font-weight: 700 !important;
    font-size: 11px !important;
    line-height: 13px !important;
  }

  :global(.transaction-learn-more-detail-text) {
    font-size: 10px !important;
    line-height: 11px !important;
  }

  .transaction {
    display: flex;
    padding: 16px 12px;
    background: #f5f6fb;
    border-radius: 12px;
    box-sizing: content-box;

    &-logo {
      background: white;
      width: 36px;
      height: 36px;
      border: 1px solid rgba(146, 162, 176, 0.3);
      border-radius: 100%;
      position: relative;
      margin-right: 10px;
      flex-shrink: 0;
    }

    &-business-logo {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: inherit;
    }

    &-action-icon {
      width: 12px;
      height: 12px;
      position: absolute;
      bottom: 0px;
      right: 0px;
      background-color: #fff;
      border-radius: 50%;
    }

    &-status {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      justify-content: center;
    }

    &-business-name {
      font-weight: 600;
      font-size: 12px;
      line-height: 15px;
      color: #0c2a57;
    }

    &-learn-more {
      margin-top: 5px;
      display: none;

      &-svg {
        margin-left: 4px;
      }

      &-button {
        display: flex;
        flex-direction: row;
        align-items: center;
      }

      &-message {
        font-weight: 500;
        font-size: 11px;
        line-height: 13px;
        color: #92a2b0;
      }
    }

    &-value {
      font-weight: bold;
      font-size: 12px;
      line-height: 15px;
      text-align: right;
    }

    &-date {
      font-weight: 400;
      font-size: 14px;
      line-height: 17px;
      text-align: right;
      color: #7a89b0;
    }

    &-info {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 8px;
    }

    &-provisional-canceled {
      -webkit-transition: all 0.3s ease;
      -moz-transition: all 0.3s ease;
      transition: all 0.3s ease;

      .transaction {
        &-logo {
          opacity: 0.4;
        }

        &-business-name {
          opacity: 0.6;
          text-decoration: line-through;
        }

        &-business-action {
          text-decoration: line-through;
        }

        &-learn-more {
          display: block;

          &-button {
            cursor: pointer;

            &:hover {
              opacity: 0.7;
            }
          }

          &-svg {
            -webkit-transition: all 0.3s ease;
            -moz-transition: all 0.3s ease;
            transition: all 0.3s ease;
          }

          &-detail {
            height: 0;
            overflow: hidden;
            padding: 0px;
            -webkit-transition: all 0.3s ease;
            -moz-transition: all 0.3s ease;
            transition: all 0.3s ease;
          }

          &.visible {
            .transaction {
              &-learn-more {
                &-svg {
                  transform: rotate(-180deg);
                }

                &-detail {
                  height: auto;
                  padding: 6px 0 0;
                }
              }
            }
          }
        }
      }
    }
  }
</style>
